import { c as _c } from "react/compiler-runtime";
import { CodeBlock, Dialog, LoadingSpinner, MarkdownText } from '@onefootprint/ui';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { languageFromPath } from '../../utils/detect-language';
import { getFilePreviewType } from '../../utils/file-preview-type';
const PdfViewer = dynamic(() => import('@/features/document-viewer/components/document-preview/components/pdf-viewer'), {
  ssr: false
});
const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result as string;
    resolve(result.split(',')[1]);
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});
type FilePreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  fileName: string;
  getFileContent: (path: string) => Promise<{
    blob: Blob;
    mimeType: string;
  }>;
};
const FilePreviewDialog = t0 => {
  const $ = _c(28);
  const {
    open,
    onClose,
    fileName,
    getFileContent
  } = t0;
  const [content, setContent] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [base64, setBase64] = useState(null);
  const [mimeType, setMimeType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== fileName || $[1] !== getFileContent || $[2] !== open) {
    t1 = () => {
      if (!open || !fileName) {
        return;
      }
      let cancelled = false;
      let previewUrl = null;
      setIsLoading(true);
      setError(null);
      setContent(null);
      setObjectUrl(null);
      setBase64(null);
      getFileContent(fileName).then(async t2 => {
        const {
          blob,
          mimeType: mime
        } = t2;
        if (cancelled) {
          return;
        }
        setMimeType(mime);
        const previewType = getFilePreviewType(fileName, mime);
        if (previewType === "image") {
          previewUrl = URL.createObjectURL(blob);
          setObjectUrl(previewUrl);
        } else {
          if (previewType === "pdf") {
            const b64 = await blobToBase64(blob);
            if (!cancelled) {
              setBase64(b64);
            }
          } else {
            const text = await blob.text();
            if (!cancelled) {
              setContent(text);
            }
          }
        }
      }).catch(e => {
        if (!cancelled) {
          setError(String(e));
        }
      }).finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
      return () => {
        cancelled = true;
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      };
    };
    $[0] = fileName;
    $[1] = getFileContent;
    $[2] = open;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== fileName || $[5] !== open) {
    t2 = [open, fileName];
    $[4] = fileName;
    $[5] = open;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== fileName || $[8] !== mimeType) {
    t3 = getFilePreviewType(fileName, mimeType);
    $[7] = fileName;
    $[8] = mimeType;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const previewType_0 = t3;
  let t4;
  if ($[10] !== base64 || $[11] !== content || $[12] !== error || $[13] !== fileName || $[14] !== isLoading || $[15] !== mimeType || $[16] !== objectUrl || $[17] !== previewType_0) {
    t4 = () => {
      if (isLoading) {
        return <div className="flex h-full items-center justify-center py-12"><LoadingSpinner /></div>;
      }
      if (error) {
        return <p className="text-error text-body-2 max-md:break-words">{error}</p>;
      }
      if (objectUrl && previewType_0 === "image") {
        return <div className="flex h-full w-full items-center justify-center"><img src={objectUrl} alt={fileName} className="max-h-full max-w-full object-contain" /></div>;
      }
      if (previewType_0 === "pdf" && base64) {
        return <div className="h-full"><PdfViewer src={`data:application/pdf;base64,${base64}`} bboxes={[]} selectedBboxIds={new Set()} onBboxClick={_temp} documentName={fileName} /></div>;
      }
      if (content != null) {
        if (mimeType === "application/json" || fileName?.endsWith(".json")) {
          try {
            const formatted = JSON.stringify(JSON.parse(content), null, 2);
            return <CodeBlock language="json">{formatted}</CodeBlock>;
          } catch {
            return <CodeBlock language="text">{content}</CodeBlock>;
          }
        }
        if (mimeType === "text/markdown" || fileName?.endsWith(".md")) {
          return <MarkdownText>{content}</MarkdownText>;
        }
        return <CodeBlock language={languageFromPath(fileName) ?? "text"}>{content}</CodeBlock>;
      }
      return null;
    };
    $[10] = base64;
    $[11] = content;
    $[12] = error;
    $[13] = fileName;
    $[14] = isLoading;
    $[15] = mimeType;
    $[16] = objectUrl;
    $[17] = previewType_0;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const renderContent = t4;
  let t5;
  if ($[19] !== renderContent) {
    t5 = renderContent();
    $[19] = renderContent;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t5) {
    t6 = <div className="h-full">{t5}</div>;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== fileName || $[24] !== onClose || $[25] !== open || $[26] !== t6) {
    t7 = <Dialog title={fileName} open={open} onClose={onClose} size="full-screen">{t6}</Dialog>;
    $[23] = fileName;
    $[24] = onClose;
    $[25] = open;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
};
export default FilePreviewDialog;
function _temp() {}